import { useBasketListPaginated, useCurrentUi } from "@keepeek/commons";
import { withSuspense } from "@keepeek/refront-components";
import { Box, FormControl, InputLabel, NativeSelect, Pagination } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { BasketsWidget as BasketsWidgetModel } from "../../../models/configuration/components/basketsWidget";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import { selectedBasketsWidgetState } from "./atoms";

const BASKET_SIZE_SELECTORS = [10, 20, 30, 40, 50];

const BasketsWidgetPaginate = function ({
  paginate,
  widgetId,
  page,
  setPage,
  size,
  setSize,
  queryFilter,
}: {
  widgetId: string;
  page: number;
  setPage: (value: number) => void;
  size: number;
  setSize: (value: number) => void;
  queryFilter: string;
} & Pick<BasketsWidgetModel, "paginate">) {
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { currentUi } = useCurrentUi();

  const { basketList } = useBasketListPaginated({
    sizeParam: size,
    pageParam: page,
    qParam: queryFilter,
    fqParam: activateBasketPartitioning ? [`uiId:${currentUi?.id}`] : [],
  });

  const setSelectedBaskets = useSetRecoilState(selectedBasketsWidgetState(widgetId));
  const { t } = useTranslation();

  const handleSetPage = (value: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSelectedBaskets([]);
    setPage(value);
  };

  const handleSetSize = (value: number) => {
    setSelectedBaskets([]);
    setSize(value);
    // Force page 1 to avoid weird behavior
    if (page !== 1) {
      setPage(1);
    }
  };

  return (
    <Box
      sx={{
        justifyContent: "center",
        alignItems: "center",
      }}
      display="flex"
      gap={4}
      className={clsx("BasketsWidget-paginate")}
    >
      {!!basketList.totalCount && basketList.totalCount > 0 && (paginate?.selectorShow ?? true) && (
        <FormControl sx={{ minWidth: (theme) => theme.spacing(12) }} variant="standard">
          <InputLabel id="simple-select-label-basket-size" htmlFor="simple-select-basket-size">
            {t("refront-components:components.pagination.sizeSelect.label")}
          </InputLabel>
          <NativeSelect
            id="simple-select-basket-size"
            defaultValue={size}
            onChange={(e) => handleSetSize(Number(e.target.value))}
          >
            {BASKET_SIZE_SELECTORS.map((s) => {
              return (
                <option key={s} value={s}>
                  {s}
                </option>
              );
            })}
          </NativeSelect>
        </FormControl>
      )}
      {!!basketList.totalCount &&
        Math.ceil(basketList.totalCount / size) > 1 &&
        (paginate?.show ?? true) && (
          <Pagination
            count={Math.ceil(basketList.totalCount / size)}
            page={page}
            onChange={(_, newPage) => handleSetPage(newPage)}
          />
        )}
    </Box>
  );
};

export default withSuspense(BasketsWidgetPaginate);
